// =============================================================================
// features/configuracoes/canais/useVendedorPairing.ts — Pareamento WAHA dos
// aparelhos dos vendedores (F39-S09).
//
// Endpoints (F39-S07), todos atrás da flag `whatsapp_sync.pairing.enabled`:
//   POST   /api/channels/waha/sessions              → inicia (idempotente)
//   GET    /api/channels/waha/sessions/:id/qr       → QR base64
//   GET    /api/channels/waha/sessions/:id/status   → estado da sessão
//   DELETE /api/channels/waha/sessions/:id          → encerra
//
// LGPD: o `qr_base64` é CREDENCIAL DE PAREAMENTO — quem o lê pareia o aparelho.
// Ele não é cacheado (`gcTime: 0`), não vai para estado global e não é logado.
// O `phone_e164` do status é PII do vendedor — só renderizado, nunca logado.
//
// Nunca useEffect+fetch — TanStack Query é o único caminho para rede.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useToast } from '../../../components/ui/Toast';
import { ApiError, api } from '../../../lib/api';

import { CHANNELS_QUERY_KEY } from './useChannels';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const PairingStatusSchema = z.enum([
  'STOPPED',
  'STARTING',
  'SCAN_QR_CODE',
  'WORKING',
  'FAILED',
]);

export type PairingStatus = z.infer<typeof PairingStatusSchema>;

const PairingSessionSchema = z.object({
  channel_id: z.string().uuid(),
  agent_id: z.string().uuid(),
  session_name: z.string(),
  status: PairingStatusSchema,
});

const PairingStatusResponseSchema = PairingSessionSchema.extend({
  phone_e164: z.string().nullable(),
});

const PairingQrSchema = z.object({
  qr_base64: z.string(),
  mime_type: z.string(),
  status: PairingStatusSchema,
});

export type PairingSession = z.infer<typeof PairingSessionSchema>;
export type PairingStatusResponse = z.infer<typeof PairingStatusResponseSchema>;
export type PairingQr = z.infer<typeof PairingQrSchema>;

// ---------------------------------------------------------------------------
// Rótulos — o operador não deve ver enum cru
// ---------------------------------------------------------------------------

export const PAIRING_STATUS_LABEL: Record<PairingStatus, string> = {
  STOPPED: 'Desconectado',
  STARTING: 'Iniciando…',
  SCAN_QR_CODE: 'Aguardando leitura do QR',
  WORKING: 'Conectado',
  FAILED: 'Falhou',
};

/** Nome da sessão de um agente — espelha `sessionNameForAgent` do backend. */
export function sessionNameForAgent(agentId: string): string {
  return `agente-${agentId}`;
}

/** Forma mínima de canal necessária para o mapeamento — evita acoplar ao DTO inteiro. */
export interface ChannelForPairing {
  readonly id: string;
  readonly provider: string;
  readonly display_handle: string | null;
}

/**
 * Mapa `nome da sessão → channelId`, só para canais WAHA.
 *
 * O backend grava `display_handle = wahaSessionId` no `connectChannelService`,
 * e o nome da sessão é `agente-<agentId>`. É esse acordo — e não uma coluna
 * exposta na API — que permite saber quem já está pareado.
 *
 * Função pura e exportada de propósito: se o backend mudar o significado de
 * `display_handle`, o teste desta função quebra antes da tela mentir que
 * ninguém está pareado.
 */
export function buildChannelBySession(channels: readonly ChannelForPairing[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const ch of channels) {
    if (ch.provider === 'waha' && ch.display_handle !== null && ch.display_handle.length > 0) {
      map.set(ch.display_handle, ch.id);
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function apiStartPairing(agentId: string): Promise<PairingSession> {
  const raw = await api.post('/api/channels/waha/sessions', { agentId });
  return PairingSessionSchema.parse(raw);
}

async function apiGetQr(channelId: string): Promise<PairingQr> {
  const raw = await api.get(`/api/channels/waha/sessions/${encodeURIComponent(channelId)}/qr`);
  return PairingQrSchema.parse(raw);
}

async function apiGetStatus(channelId: string): Promise<PairingStatusResponse> {
  const raw = await api.get(`/api/channels/waha/sessions/${encodeURIComponent(channelId)}/status`);
  return PairingStatusResponseSchema.parse(raw);
}

async function apiStopPairing(channelId: string): Promise<void> {
  await api.delete(`/api/channels/waha/sessions/${encodeURIComponent(channelId)}`);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const PAIRING_QUERY_KEY = ['waha-pairing'] as const;

const qrKey = (channelId: string): readonly unknown[] => [...PAIRING_QUERY_KEY, 'qr', channelId];
const statusKey = (channelId: string): readonly unknown[] => [
  ...PAIRING_QUERY_KEY,
  'status',
  channelId,
];

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Inicia (ou reaproveita) o pareamento de um vendedor. */
export function useStartPairing(): ReturnType<typeof useMutation<PairingSession, Error, string>> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<PairingSession, Error, string>({
    mutationFn: apiStartPairing,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHANNELS_QUERY_KEY.all });
    },
    onError: (err) => {
      toast(
        err instanceof ApiError && err.status === 503
          ? 'Integração WhatsApp não configurada neste ambiente.'
          : 'Não foi possível iniciar o pareamento.',
        'danger',
      );
    },
  });
}

/**
 * QR do canal. Só busca quando `enabled` — o QR é credencial e não deve ser
 * pedido "por precaução".
 *
 * `gcTime: 0` e `staleTime: 0`: nada de QR sobrando no cache do TanStack depois
 * que o modal fecha.
 */
export function usePairingQr(
  channelId: string | null,
  enabled: boolean,
): ReturnType<typeof useQuery<PairingQr>> {
  return useQuery<PairingQr>({
    queryKey: qrKey(channelId ?? 'none'),
    queryFn: () => apiGetQr(channelId as string),
    enabled: enabled && channelId !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Status da sessão, com polling enquanto o modal estiver aberto.
 *
 * O polling PARA sozinho quando a sessão chega em WORKING ou FAILED — não há por
 * que continuar batendo depois que o desfecho aconteceu.
 */
export function usePairingStatus(
  channelId: string | null,
  enabled: boolean,
): ReturnType<typeof useQuery<PairingStatusResponse>> {
  return useQuery<PairingStatusResponse>({
    queryKey: statusKey(channelId ?? 'none'),
    queryFn: () => apiGetStatus(channelId as string),
    enabled: enabled && channelId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'WORKING' || status === 'FAILED') return false;
      return 3000;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Encerra o pareamento (logout no WAHA + soft-delete do canal). */
export function useStopPairing(): ReturnType<typeof useMutation<void, Error, string>> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, string>({
    mutationFn: apiStopPairing,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHANNELS_QUERY_KEY.all });
      toast('Aparelho desconectado.', 'success');
    },
    onError: () => {
      toast('Não foi possível desconectar o aparelho.', 'danger');
    },
  });
}
